import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import axiosInstance from "../../../interceptors";
import EmptyState from "../../../components/shared/EmptyState";
import LoadingState from "../../../components/shared/LoadingState";
import ErrorState from "../../../components/shared/ErrorState";
import { toast } from "../../../components/shared/toast";

// Career-page list. Read-only table backed by GET /job-posting/career-pages.
// Each row exposes: View public link, Edit, Publish/Unpublish toggle, Archive.
export default function CareerPagesList() {
  const nav = useNavigate();
  const [pages, setPages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [errorStatus, setErrorStatus] = useState(null);
  const [busyId, setBusyId] = useState(null);

  const load = async () => {
    setLoading(true);
    setError(null);
    setErrorStatus(null);
    try {
      const { data } = await axiosInstance.get("/job-posting/career-pages");
      setPages(Array.isArray(data?.pages) ? data.pages : []);
    } catch (e) {
      setError(e?.message || e?.data?.error || "Failed to load");
      setErrorStatus(e?.status || e?.response?.status || 500);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const togglePublish = async (p) => {
    setBusyId(p.id);
    try {
      const verb = p.isPublished ? "unpublish" : "publish";
      await axiosInstance.post(`/job-posting/career-pages/${p.id}/${verb}`);
      toast.success(p.isPublished ? "Unpublished" : "Published");
      await load();
    } catch (e) {
      toast.error("Couldn't toggle publish", { description: e?.message });
    } finally {
      setBusyId(null);
    }
  };

  const archive = async (p) => {
    if (!window.confirm(`Archive "${p.title}"? Public URL stops resolving.`)) return;
    setBusyId(p.id);
    try {
      await axiosInstance.delete(`/job-posting/career-pages/${p.id}`);
      toast.success(`Archived "${p.title}"`);
      await load();
    } catch (e) {
      toast.error("Archive failed", { description: e?.message });
    } finally {
      setBusyId(null);
    }
  };

  return (
    <div className="page-enter">
      <div className="flex items-end justify-between mb-6">
        <div>
          <h1 className="text-2xl font-semibold text-vf-base">Career pages</h1>
          <p className="text-sm text-vf-muted mt-1">
            Branded landing pages at <code className="text-xs">/careers/&#123;slug&#125;</code>.
          </p>
        </div>
        <button
          onClick={() => nav("/admin/career-pages/new")}
          className="px-3 py-1.5 rounded bg-vf-primary text-white text-sm font-medium"
        >
          + New career page
        </button>
      </div>

      {error && !loading ? (
        <ErrorState status={errorStatus} description={errorStatus === 403 ? undefined : error} retry={load} />
      ) : (
      <div className="bg-white border border-slate-200 rounded-xl overflow-hidden">
        {loading && <div className="p-4"><LoadingState variant="skeleton" shape="row" count={3} /></div>}
        {!loading && pages.length === 0 && (
          <EmptyState
            title="No career pages yet"
            description="Build a branded landing page at /careers/{slug} so candidates can browse and apply directly."
            primaryAction={{ label: "+ New career page", to: "/admin/career-pages/new" }}
          />
        )}
        {!loading && pages.length > 0 && (
          <table className="w-full text-sm">
            <thead className="bg-slate-50 border-b border-slate-200">
              <tr>
                <Th>Slug</Th>
                <Th>Title</Th>
                <Th>Status</Th>
                <Th>Updated</Th>
                <Th className="text-right">Actions</Th>
              </tr>
            </thead>
            <tbody>
              {pages.map((p) => (
                <tr key={p.id} className="border-t border-slate-100">
                  <td className="px-3 py-2 font-mono text-xs">{p.slug}</td>
                  <td className="px-3 py-2 font-medium">{p.title}</td>
                  <td className="px-3 py-2">
                    {p.isPublished ? (
                      <span className="text-[11px] px-1.5 py-0.5 rounded bg-emerald-50 text-emerald-700 font-medium">
                        Published
                      </span>
                    ) : (
                      <span className="text-[11px] px-1.5 py-0.5 rounded bg-slate-100 text-slate-600 font-medium">
                        Draft
                      </span>
                    )}
                  </td>
                  <td className="px-3 py-2 text-xs text-vf-muted">
                    {p.updatedAt ? new Date(p.updatedAt).toLocaleString() : "—"}
                  </td>
                  <td className="px-3 py-2 text-right">
                    <div className="inline-flex gap-1.5">
                      <a
                        href={`/careers/${p.slug}`}
                        target="_blank"
                        rel="noreferrer"
                        className="px-2 py-1 rounded border border-slate-300 text-xs"
                      >
                        View ↗
                      </a>
                      <button
                        onClick={() => nav(`/admin/career-pages/${p.id}`)}
                        className="px-2 py-1 rounded border border-slate-300 text-xs"
                      >
                        Edit
                      </button>
                      <button
                        disabled={busyId === p.id}
                        onClick={() => togglePublish(p)}
                        className="px-2 py-1 rounded border border-slate-300 text-xs disabled:opacity-50"
                      >
                        {busyId === p.id ? "…" : p.isPublished ? "Unpublish" : "Publish"}
                      </button>
                      <button
                        disabled={busyId === p.id}
                        onClick={() => archive(p)}
                        className="px-2 py-1 rounded border border-red-300 text-red-700 text-xs disabled:opacity-50"
                      >
                        Archive
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
      )}
    </div>
  );
}

const Th = ({ children, className = "" }) => (
  <th className={`px-3 py-2 text-left text-[11px] uppercase tracking-wide text-vf-muted font-semibold ${className}`}>
    {children}
  </th>
);
